import { SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, notifications, UseFormReturnType } from 'context'

import { LessonsContext } from '../../context/LessonsContext'
import { useMantineTheme, useMediaQuery, useToggle } from 'hooks'
import {
  Modal,
  Stack,
  Box,
  Grid,
  Button,
  TextInput,
  Checkbox,
  Title,
  Text,
  Anchor,
  RichTextArea,
  Divider,
  DateInput,
  Transition,
  Flex,
  DayRender,
} from 'components'
import { CardSkillsStandardsActivity } from '../CardSkillsStandardsActivity'
import { IActivityModal } from './interfaces'
import { IconX, IconCheck } from '@tabler/icons-react'
import { useStyles } from './styles'
import { ILvl4Item } from 'interfaces'
import { CriteriaModal } from '../CriteriaModal'
import {
  createBlankActivity,
  getActivity,
  updateActivity,
  createBlankActivityTask,
  deleteActivity,
} from '../../../../pages/NewProject/service/projects'
import { v4 as Uuidv4 } from 'uuid'
import { IconBooks, IconMoodCog } from '@tabler/icons-react'
import WrappFiles from './FileUploader/WrappFiles'
import WrappTaskFiles from './FileUploader/WrappTaskFiles'
import dayjs from 'dayjs'
import { modals } from '@mantine/modals'
import { FocusTrap } from '@mantine/core'
import { ConfirmAssessmentChangeModal } from 'components/ConfirmAssessmentChangeModal'
import { Loading } from '@legacy/common/_components'

export interface IActivityFormValues {
  title: string
  planning?: string
  has_assessments?: boolean
  student_task_title?: string
  student_task_description?: string
  student_task_date?: string | number | Date
}

const initialValues = {
  title: '',
  planning: '',
  has_assessments: false,
  student_task_title: '',
  student_task_description: '',
  student_task_date: '',
}

export const ActivityModal = ({ projectID, activity, skills, contents, onSaveActivity }: IActivityModal) => {
  const { t, i18n } = useTranslation()
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const [selectSkillsAction, toggleSelectSkillsAction] = useToggle([true, false])
  const [selectStandardsAction, toggleSelectStandardsAction] = useToggle([true, false])
  const [assessedActivities, setAssessedActivities] = useState<boolean>(false)
  const [deliverableChecked, setDeliverableChecked] = useState<boolean>(false)
  const [temporaryId, setTemporaryId] = useState<string>('')
  const [initialSkills, setInitialSkills] = useState<ILvl4Item[]>([])
  const [initialContents, setInitialContents] = useState<ILvl4Item[]>([])
  const [lv4Items, setActivityLv4Items] = useState<any[]>([])
  const [editedCriteria, setEditedCriteria] = useState<any[]>([])
  const [temporaryTaskId, setTemporaryTaskId] = useState<string>('')
  // AR-670: assignment creation was removed from the backend (read-only
  // integration) — the Classroom switch and its payload flag are gone.
  const dateValueFormat = i18n.language === 'pt-BR' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'
  const [initalStateFromServer, setInitalStateFromServer] = useState<any>(null)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isNewActivity, setIsNewActivity] = useState<boolean>(false)
  const [planningError, setPlanningError] = useState<boolean>(false)
  const [showAssessmentModal, setShowAssessmentModal] = useState<boolean>(false)
  // isNewActivity only flips to false after the PATCH resolves, so a Cancel while the
  // save is still in flight would reach the discard below and delete the activity the
  // PATCH had just persisted - by id, so having a title no longer protects it.
  const isSavingRef = useRef(false)
  // The modal is only mounted while it is open (LessonsList renders it
  // conditionally), so closing discards every piece of state. Responses that
  // land after the unmount must not write into a future session: a stale
  // temporaryTaskId made every following Save 404, and a stale
  // isNewActivity/temporaryId pair made a later edit-cancel DELETE the very
  // activity being edited.
  const unmountedRef = useRef(false)
  useEffect(
    () => () => {
      unmountedRef.current = true
    },
    [],
  )

  const successMessage = t('new_project_page.activity_added_message')
  const editedSuccessMessage = t('new_project_page.activity_edited_message')
  const successColor = 'green'
  const successIcon = <IconCheck size={24} />
  const errorMessage = t('new_project_page.activity_added_error_message')
  const errorColor = 'red'
  const errorIcon = <IconX size={24} />
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const {
    openedActivityModal,
    closeActivityModal,
    activitySkills,
    setActivitySkills,
    activityStandards,
    setActivityStandards,
    selectedSkillsHandler,
    selectedStandardsHandler,
    selectedSkills,
    selectedStandards,
    parentLessonId,
    editActivity,
    setEditActivity,
  } = useContext(LessonsContext)

  const handleChangeAllSkills = useCallback(() => {
    if (selectSkillsAction) {
      selectedSkillsHandler.setState(activitySkills)
    } else {
      selectedSkillsHandler.setState([])
    }
  }, [selectSkillsAction, activitySkills, selectedSkills])

  const handleChangeAllStandards = useCallback(() => {
    if (selectStandardsAction) {
      selectedStandardsHandler.setState(activityStandards)
    } else {
      selectedStandardsHandler.setState([])
    }
  }, [selectStandardsAction, activityStandards, selectedStandards])

  const handleToggleAllSkills = useCallback(() => {
    handleChangeAllSkills()
    toggleSelectSkillsAction()
  }, [selectSkillsAction, handleChangeAllSkills])

  const handleToggleAllStandards = useCallback(() => {
    handleChangeAllStandards()
    toggleSelectStandardsAction()
  }, [selectStandardsAction, handleChangeAllStandards])

  useEffect(() => {
    setActivitySkills(skills)
    setActivityStandards(contents)
  }, [skills, contents])

  useEffect(() => {
    setActivitySkills(skills)
    setActivityStandards(contents)
  }, [])

  const handleEvaluativeCriteria = (value: any) => {
    const index = editedCriteria.findIndex((item: any) => item._id === value._id)

    setEditedCriteria(
      index > -1 ? editedCriteria.map((item, i) => (i === index ? value : item)) : [...editedCriteria, value],
    )
  }

  const setFormFields = (
    res: { title: any; planning: any },
    form: UseFormReturnType<IActivityFormValues, (values: IActivityFormValues) => IActivityFormValues>,
  ) => {
    form.setFieldValue('title', res.title)
    form.setFieldValue('planning', res.planning)
  }

  const handleAssessments = (
    res: { hasAssessments: boolean | ((prevState: boolean) => boolean) },
    form: UseFormReturnType<IActivityFormValues, (values: IActivityFormValues) => IActivityFormValues>,
  ) => {
    if (typeof res.hasAssessments === 'boolean') {
      form.setFieldValue('has_assessments', res?.hasAssessments)
      setAssessedActivities(res.hasAssessments)
    }
  }

  const handleTasks = (
    res: {
      hasTasks: any
      studentTask: {
        id: SetStateAction<string>
        title: any
        description: any
        date: string | number | Date
      }
    },
    form: UseFormReturnType<IActivityFormValues, (values: IActivityFormValues) => IActivityFormValues>,
  ) => {
    if (res.hasTasks) {
      setDeliverableChecked(true)
      // Keep the string invariant: an edit payload with hasTasks but no task id
      // would otherwise store undefined and temporaryTaskId.length crashes render
      setTemporaryTaskId(res.studentTask?.id ?? '')

      form.setFieldValue('student_task_title', res.studentTask?.title)
      form.setFieldValue('student_task_description', res.studentTask?.description)
      if (res.studentTask?.date) form.setFieldValue('student_task_date', new Date(res.studentTask?.date))
    } else {
      setDeliverableChecked(false)
      setTemporaryTaskId('')
    }
  }

  const setInitialStates = (res: {
    contents: SetStateAction<ILvl4Item[]>
    skills: SetStateAction<ILvl4Item[]>
    activityLv4Items: SetStateAction<any[]>
  }) => {
    if (activitySkills.length === res.skills.length) {
      toggleSelectSkillsAction()
    }

    if (activityStandards.length === res.contents.length) {
      toggleSelectStandardsAction()
    }

    selectedStandardsHandler.setState(res?.contents)
    selectedSkillsHandler.setState(res?.skills)
    setInitialSkills(res?.skills)
    setInitialContents(res?.contents)
    setActivityLv4Items(res.activityLv4Items)
  }

  useEffect(() => {
    if (openedActivityModal) {
      // Limpa rúbricas anteriores ao abrir modal para garantir dados frescos
      setActivityLv4Items([])
      setEditedCriteria([])
      isSavingRef.current = false

      const id = projectID
      const hasEditActivity = editActivity && editActivity.id

      if (hasEditActivity) setIsEditing(true)

      if (hasEditActivity) {
        setTemporaryId(editActivity.id)
        getActivity(editActivity.id).then(res => {
          if (unmountedRef.current) return
          // Normalize the snapshot to the same shape the form will end up with
          // (RichTextArea coerces null/'' to '<p></p>' and Tiptap also strips
          // attribute-less tags like <span>, has_assessments is a boolean,
          // etc) so that formDidChanges does not produce false positives when
          // nothing has actually been edited.
          // Normalize HTML the same way Tiptap will after mount: strip
          // attribute-less inline wrappers and collapse whitespace before
          // closing tags. Used both for the snapshot and the dirty check.
          const stripEmptyInlineTags = (html?: string | null) =>
            (html ?? '<p></p>')
              .replace(/<span(?:\s[^>]*)?>/g, '')
              .replace(/<\/span>/g, '')
              .replace(/\s+(<\/[a-z]+>)/gi, '$1')
              .replace(/(<[a-z]+>)\s+/gi, '$1')
              .replace(/\s+/g, ' ')
              .trim()
          const normalized = {
            title: res.title ?? '',
            planning: stripEmptyInlineTags(res.planning),
            has_assessments: typeof res.hasAssessments === 'boolean' ? res.hasAssessments : false,
            student_task_title: res.studentTask?.title ?? '',
            student_task_description: stripEmptyInlineTags(res.studentTask?.description),
          }
          setInitalStateFromServer({ ...res, ...normalized })
          form.setValues(values => ({ ...values, ...normalized }))
          handleAssessments(res, form)
          handleTasks(res, form)
          setInitialStates(res)
          setIsLoading(false)
        })
      } else {
        form.reset()
      }

      if (id && !hasEditActivity) {
        createBlankActivity({
          session_meeting_id: parentLessonId,
          project_id: id,
        }).then(res => {
          if (unmountedRef.current) {
            // The teacher closed the modal before this answer arrived: discard
            // the blank right away instead of leaving an orphan behind.
            deleteActivity(res.id)
            return
          }
          setTemporaryId(res.id)
          setIsNewActivity(true)
          setIsLoading(false)
        })
      }
    }

    if (!openedActivityModal) {
      if (!selectSkillsAction) {
        toggleSelectSkillsAction()
      }

      if (!selectStandardsAction) {
        toggleSelectStandardsAction()
      }
    }
  }, [openedActivityModal])

  const form = useForm<IActivityFormValues>({
    initialValues,
    validate: {
      title: value => (!value && value.length < 3 ? t('required_field') : null),
      planning: value => {
        const strippedValue = value ? value.replace(/<[^>]*>/g, '') : ''
        if (!strippedValue || strippedValue.length < 3) {
          setPlanningError(true)
          return t('required_field')
        }
        setPlanningError(false)
        return null
      },
      has_assessments: () => {
        if (!assessedActivities) return null

        const isEmptySkillsAndStandards = selectedSkills.length === 0 && selectedStandards.length === 0
        const lv4NewItems = [...transformToLv4Items(selectedSkills), ...transformToLv4Items(selectedStandards)]
        const lv4NewIds = lv4NewItems.map(item => item.lv4_item_id)
        const removedItems = [...initialContents, ...initialSkills].filter(item => !lv4NewIds.includes(item.id))
        const itemsToRemove = findItemsToRemove(lv4Items, removedItems)

        if (isEmptySkillsAndStandards && itemsToRemove.length === 0) {
          return t('missing_skills')
        }

        if (
          itemsToRemove.length === initalStateFromServer?.skills?.length + initalStateFromServer?.contents?.length &&
          isEmptySkillsAndStandards
        ) {
          return t('missing_skills')
        }
      },
      ...(deliverableChecked && {
        student_task_title: value => (!value || value.length < 3 ? t('required_field') : null),
        student_task_date: value => (!value || String(value).length < 3 ? t('required_field') : null),
        student_task_description: value => {
          const strippedValue = value ? value.replace(/<[^>]*>/g, '') : ''
          return !strippedValue || strippedValue.length < 3 ? t('required_field') : null
        },
      }),
    },
  })

  const handleForm = async (values: IActivityFormValues) => {
    const errors = form.validate()

    if (errors.hasErrors) {
      handleErrors(errors)
    } else {
      onSaveActivity()
      handleSuccess(values)
    }
  }

  // Surface the validation message we actually produced instead of the generic one.
  // Enabling assessment with no curriculum item selected is the only rule the teacher
  // cannot guess: its inline error sits beside a checkbox that is normally scrolled
  // out of view, so "Fill the required fields" made Save look like it did nothing.
  // Plain "Required field" messages stay generic since the field itself is marked.
  const handleErrors = (validation: { errors?: Record<string, unknown> } | unknown) => {
    const fieldErrors = (validation as { errors?: Record<string, unknown> })?.errors ?? {}
    const actionable = Object.values(fieldErrors).filter(
      (message): message is string => typeof message === 'string' && message !== t('required_field'),
    )

    notifications.show({
      message: actionable.length > 0 ? [...new Set(actionable)].join(' ') : t('fill_the_required_fields'),
      color: 'red',
      icon: <IconX size={24} />,
    })
  }

  const transformToLv4Items = (inputItems: any) => {
    const transformedItems: any[] = inputItems.map((item: any) => {
      return {
        lv4_item_id: item.id,
      }
    })

    return transformedItems
  }

  const findItemsToRemove = (
    initialData: { id: string; lv4ItemId: string }[] | null | undefined,
    dataToRemove: { id: string }[] | null | undefined,
  ): { id: string; _destroy: number }[] => {
    if (!Array.isArray(initialData) || !Array.isArray(dataToRemove)) return []

    const result = initialData
      .filter(
        item =>
          item &&
          typeof item === 'object' &&
          dataToRemove.some(
            removeItem => removeItem && typeof removeItem === 'object' && removeItem.id === item.lv4ItemId,
          ),
      )
      .map(item => ({
        id: item.id,
        _destroy: 1,
      }))

    return result
  }

  const lv4ItemsAtributesPayload = () => {
    const lv4NewItems = [...transformToLv4Items(selectedSkills), ...transformToLv4Items(selectedStandards)]
    const idsToFilter = [...initialContents, ...initialSkills].map(item => item.id)
    const filteredLv4NewItems = lv4NewItems.filter(item => !idsToFilter.includes(item.lv4_item_id))

    const lv4NewIds = lv4NewItems.map(item => item.lv4_item_id)
    const removedItems = [...initialContents, ...initialSkills].filter(item => !lv4NewIds.includes(item.id))
    const itemsToRemove = findItemsToRemove(lv4Items, removedItems)

    const mergedPayloadWithLv4ItemsChangedAndToRemove = [...filteredLv4NewItems, ...itemsToRemove]

    const addsEvaluateCriteriaToPayload = (p: any, curricular: any[]) => {
      return curricular.reduce(
        (acc, curricularItem) => {
          const index = acc.findIndex((item: { lv4_item_id: any }) => item.lv4_item_id === curricularItem._id)

          if (index > -1) {
            acc[index] = {
              ...acc[index],
              assessment_instrument_id: null,
              activity_lv4_item_how_to_assesses_attributes: curricularItem.activity_lv4_item_how_to_assesses_attributes,
            }
          } else {
            const obj = lv4Items?.find((objeto: { lv4ItemId: any }) => objeto.lv4ItemId === curricularItem._id)
            acc.push({
              id: obj?.id,
              assessment_instrument_id: null,
              activity_lv4_item_how_to_assesses_attributes: curricularItem.activity_lv4_item_how_to_assesses_attributes,
            })
          }

          return acc
        },
        [...p],
      )
    }

    return addsEvaluateCriteriaToPayload(mergedPayloadWithLv4ItemsChangedAndToRemove, editedCriteria)
  }

  const handleSuccess = async (values: any) => {
    const payload = {
      id: temporaryId,
      project_id: projectID,
      title: values.title,
      planning: values.planning,
      has_assessments: assessedActivities,
      activity_lv4_items_attributes: lv4ItemsAtributesPayload(),
      ...(deliverableChecked === true
        ? {
            has_tasks: true,
            student_task_attributes: {
              id: temporaryTaskId,
              title: values?.student_task_title,
              description: values?.student_task_description,
              date: dayjs(values?.student_task_date).format('YYYY-MM-DD'),
            },
          }
        : { has_tasks: false }),
    }

    // updateActivity re-throws on any non-2xx, so without this catch a failed save
    // rejected here and the user got no feedback at all: the modal stayed open with
    // the filled form and clicking Save appeared to do nothing.
    isSavingRef.current = true
    let response
    try {
      response = await updateActivity(temporaryId, payload)
    } catch (error: any) {
      // 404/422/500/401 all end up as the same generic toast, so the status and
      // body logged here are the only field clue to which failure actually happened.
      console.error('Activity save failed', error?.response?.status, error?.response?.data, error)
      response = undefined
    }

    if (!response || response.status !== 200) {
      // Nothing was persisted, so the placeholder is still disposable on Cancel.
      isSavingRef.current = false

      notifications.show({
        message: errorMessage,
        color: errorColor,
        icon: errorIcon,
      })

      return
    }

    setEditActivity(null)
    setIsNewActivity(false)
    // resetValues instead of a plain close: after saving an EDIT, isNewActivity
    // stays false (no state transition) so the reset effect below never runs and
    // temporaryTaskId/deliverableChecked/isEditing leaked into the next
    // "Add activity", whose PATCH then reused the edited activity's student_task
    // id and failed with 404. Safe here: isSavingRef is still true, which keeps
    // resetValues from discarding the activity the PATCH just persisted.
    resetValues()

    const notifyMessage = isEditing ? editedSuccessMessage : successMessage

    notifications.show({
      message: notifyMessage,
      color: successColor,
      icon: successIcon,
    })
  }

  const updateField = (name: string) => (value: string) => {
    form.setFieldValue(name, value)
    if (name === 'planning' && planningError) {
      setPlanningError(false)
    }
  }

  const resetValues = () => {
    setTemporaryId('')
    // This modal never unmounts (LessonsList renders it unconditionally), so any
    // state not cleared here leaks into the next "Add activity". A stale
    // temporaryTaskId skips the create_blank_student_task effect and the PATCH
    // then sends a student_task id belonging to another activity, which Rails
    // rejects with RecordNotFound (404) on every Save until a full page reload.
    setTemporaryTaskId('')
    // isEditing leaked the same way: a new activity created after an edit showed
    // the "edited" toast and ran the edit branch of the dirty check
    setIsEditing(false)
    setInitialSkills([])
    setInitialContents([])
    setActivityLv4Items([])
    setEditedCriteria([])
    setDeliverableChecked(false)
    setAssessedActivities(false)
    closeActivityModal()
    setPlanningError(false)
    form.reset()
    form.resetDirty()
    setIsLoading(true)

    // Discard only the blank this modal created. removeBlankActivity wipes every
    // title-less activity of the whole lesson, so any other modal open on the same
    // lesson (another tab, another teacher, or a fast cancel-then-reopen) lost its
    // own blank and the PATCH on Save came back 404.
    // Never once a save is under way: the PATCH may already have persisted this id.
    if (isNewActivity && temporaryId && !isSavingRef.current) {
      deleteActivity(temporaryId)
      setIsNewActivity(false)
    }
  }

  const formDidChanges = () => {
    const changelv4 = lv4ItemsAtributesPayload() || []
    if (changelv4.length > 0) return true

    const initialFormData = initialValues

    if (!isEditing) {
      const emptyPString = '<p></p>'
      if (form.values.has_assessments !== initialFormData.has_assessments) return true

      if (form.values.planning !== initialFormData.planning && form.values.planning !== emptyPString) return true

      return (
        form.values.title !== initialFormData.title ||
        (deliverableChecked &&
          (form.values.student_task_title !== initialFormData.student_task_title ||
            form.values.student_task_description !== initialFormData.student_task_description))
      )
    } else {
      // Tiptap normalizes HTML on mount in ways regex cannot reproduce
      // (entity encoding, attribute reordering, empty wrapper stripping,
      // whitespace collapse). The canonical workaround from the
      // Tiptap/ProseMirror community is to compare the text content
      // via DOMParser instead of the raw HTML.
      const toText = (html?: string | null) => {
        if (!html) return ''
        const doc = new DOMParser().parseFromString(html, 'text/html')
        return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
      }
      const titleDiff = (form.values.title ?? '').trim() !== (initalStateFromServer.title ?? '').trim()
      const planningDiff = toText(form.values.planning) !== toText(initalStateFromServer.planning)
      const haDiff = form.values.has_assessments !== initalStateFromServer.has_assessments
      const sttDiff = (form.values.student_task_title ?? '').trim() !== (initalStateFromServer.student_task_title ?? '').trim()
      const stdDiff = toText(form.values.student_task_description) !== toText(initalStateFromServer.student_task_description)
      return titleDiff || planningDiff || haDiff || (deliverableChecked && (sttDiff || stdDiff))
    }

    return false
  }

  const onClose = () => {
    const hasChanges = formDidChanges()

    if (hasChanges) {
      modals.openConfirmModal({
        title: t('general_changed_confirmation_modal_title'),
        size: 'lg',
        children: <Text size='sm'>{t('general_changed_confirmation_modal_message')}</Text>,
        labels: {
          confirm: t('new_project_page.confirm'),
          cancel: t('new_project_page.warnning_groupt_button1'),
        },
        confirmProps: { size: 'lg' },
        cancelProps: { size: 'lg' },
        onConfirm: resetValues,
      })
    } else {
      resetValues()
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (deliverableChecked && projectID && projectID !== '' && projectID !== null) {
        const id = await createBlankActivityTask({
          id: temporaryId,
          project_id: projectID,
        })
        return id
      }
    }
    if (deliverableChecked && temporaryTaskId === '') {
      fetchData().then(res => {
        if (unmountedRef.current) return
        // createBlankActivityTask swallows request errors and resolves undefined;
        // without this guard res.id throws and temporaryTaskId stays empty forever.
        if (res?.id) {
          setTemporaryTaskId(res.id)
        }
      })
    }
    // temporaryTaskId in the deps re-arms this effect after resetValues clears it,
    // so a new blank student_task is created for the next activity of the modal.
  }, [deliverableChecked, temporaryTaskId])

  const searchItemIfItExistsbyEditingCriteriaId = (editingCriteria: string) => {
    if (!Array.isArray(editedCriteria) || !Array.isArray(lv4Items)) {
      return null
    }

    return (
      editedCriteria.find((item: any) => item._id === editingCriteria) ||
      lv4Items.find((item: any) => item.lv4ItemId === editingCriteria) ||
      null
    )
  }

  useEffect(() => {
    if (activityStandards.length === selectedStandards.length) {
      toggleSelectStandardsAction(false)
    } else {
      toggleSelectStandardsAction(true)
    }
  }, [selectedStandards])

  useEffect(() => {
    if (activitySkills.length === selectedSkills.length) {
      toggleSelectSkillsAction(false)
    } else {
      toggleSelectSkillsAction(true)
    }
  }, [selectedSkills])

  const handleAssessmentChange = () => {
    const hasScores = initalStateFromServer?.hasScores || false

    if (assessedActivities && hasScores) {
      setShowAssessmentModal(true)
      return
    }

    setAssessedActivities(prevState => !prevState)
  }

  return (
    <Modal
      key={activity ? activity.id : temporaryId}
      zIndex={150}
      size='lg'
      opened={openedActivityModal}
      onClose={onClose}
      title={activity ? t('new_project_page.edit_activity') : t('new_project_page.add_activity')}
      fullScreen={true}
      classNames={classes}
    >
      {isLoading && <Loading />}

      {!isLoading && (
        <>
          <CriteriaModal
            handleEvaluativeCriteria={handleEvaluativeCriteria}
            lv4Items={lv4Items}
            editedCriteria={editedCriteria}
            searchItem={searchItemIfItExistsbyEditingCriteriaId}
          />
          <ConfirmAssessmentChangeModal
            opened={showAssessmentModal}
            onClose={() => setShowAssessmentModal(false)}
            action={() => setAssessedActivities(prevstate => !prevstate)}
          />
          <div>
            <FocusTrap active>
              <Stack className={classes.stack}>
                <TextInput
                  data-autofocus
                  name='title'
                  size='md'
                  label={t('new_project_page.activity_title_label')}
                  withAsterisk
                  {...form.getInputProps('title')}
                />
                <Stack spacing={0}>
                  <div
                    style={
                      planningError
                        ? {
                            border: '1px solid red',
                            borderRadius: '10px',
                          }
                        : {}
                    }
                  >
                    <RichTextArea
                      name='planning'
                      required
                      size='md'
                      label={t('new_project_page.activity_planning_label')}
                      description={t('new_project_page.activity_planning_description')}
                      withAsterisk
                      {...form.getInputProps('planning')}
                      onChange={updateField('planning')}
                    />
                  </div>
                  {planningError && (
                    <Text color='red' size='sm'>
                      {t('required_field')}
                    </Text>
                  )}
                </Stack>
                <Divider my='xs' />
                {temporaryId && temporaryId.length > 0 && projectID && projectID !== '' && (
                  <WrappFiles activityId={temporaryId} projectId={projectID} />
                )}
                {(activitySkills && activitySkills.length > 0) ||
                (activityStandards && activityStandards.length > 0) ? (
                  <Stack spacing={0}>
                    <Title order={4}>{t('new_project_page.activity_assessment_title')}</Title>
                    <Text color='dimmed'>{t('new_project_page.activity_assessment_description')}</Text>
                    <Checkbox
                      mt={4}
                      size='lg'
                      label={t('new_project_page.activity_assessment_checkbox_label')}
                      checked={assessedActivities}
                      {...form.getInputProps('has_assessments')}
                      onChange={handleAssessmentChange}
                    />
                  </Stack>
                ) : null}
                {activitySkills && activitySkills.length > 0 && (
                  <>
                    <Stack spacing={0} key={temporaryTaskId}>
                      <Title order={4}>{t('new_project_page.title_curriculum_items')}</Title>
                      <Text color='dimmed'>{t('new_project_page.activity_curriculum_items_description')}</Text>
                    </Stack>
                    <Stack align='flex-start' spacing='sm'>
                      <Flex>
                        <IconMoodCog size='1.5rem' />
                        <Title order={5} ml='xs'>
                          {t('new_project_page.title_skills')}
                        </Title>
                      </Flex>
                      <Anchor align='left' component='button' onClick={() => handleToggleAllSkills()}>
                        {selectSkillsAction
                          ? t('new_project_page.activity_add_all_items')
                          : t('new_project_page.activity_remove_all_items')}
                      </Anchor>
                    </Stack>
                  </>
                )}

                <Grid>
                  {activitySkills &&
                    activitySkills.length > 0 &&
                    activitySkills.map(item => {
                      return (
                        <Grid.Col key={Uuidv4()} span={12} xs={12} sm={6} md={6} lg={4}>
                          <CardSkillsStandardsActivity
                            item={item}
                            assessed={assessedActivities}
                            editedCriteria={editedCriteria}
                            type='skill'
                            isEditing={searchItemIfItExistsbyEditingCriteriaId(item.id)}
                          />
                        </Grid.Col>
                      )
                    })}
                </Grid>

                {activityStandards && activityStandards.length > 0 && (
                  <>
                    <Stack align='flex-start' spacing='sm' key={temporaryTaskId + '2'}>
                      <Flex>
                        <IconBooks size='1.5rem' />
                        <Title order={5} ml='xs'>
                          {t('new_project_page.title_standards')}
                        </Title>
                      </Flex>
                      <Anchor align='left' component='button' onClick={() => handleToggleAllStandards()}>
                        {selectStandardsAction
                          ? t('new_project_page.activity_add_all_items')
                          : t('new_project_page.activity_remove_all_items')}
                      </Anchor>
                    </Stack>
                  </>
                )}

                <Grid>
                  {activityStandards &&
                    activityStandards.length > 0 &&
                    activityStandards.map(item => {
                      return (
                        <Grid.Col key={Uuidv4()} span={12} xs={12} sm={6} md={6} lg={4}>
                          <CardSkillsStandardsActivity
                            item={item}
                            assessed={assessedActivities}
                            editedCriteria={editedCriteria}
                            type='content'
                            isEditing={searchItemIfItExistsbyEditingCriteriaId(item.id)}
                          />
                        </Grid.Col>
                      )
                    })}
                </Grid>
                <Divider my='xs' />
                <Stack spacing={0}>
                  <Title order={4}>{t('new_project_page.activity_deliverable_title')}</Title>
                  <Text color='dimmed'>{t('new_project_page.activity_deliverable_description')}</Text>
                  <Checkbox
                    checked={deliverableChecked}
                    onChange={() => setDeliverableChecked(!deliverableChecked)}
                    mt={4}
                    size='lg'
                    label={t('new_project_page.activity_deliverable_checkbox_label')}
                  />
                </Stack>
                <Transition mounted={deliverableChecked} transition='fade' duration={300} timingFunction='ease'>
                  {styles => (
                    <Stack spacing='md' style={styles}>
                      <TextInput
                        name='student_task_title'
                        size='md'
                        label={t('new_project_page.activity_deliverable_title_label')}
                        withAsterisk
                        required
                        {...form.getInputProps('student_task_title')}
                      />
                      <DateInput
                        name='student_task_date'
                        size='md'
                        label={t('new_project_page.activity_deliverable_date_label')}
                        withAsterisk
                        locale={i18n.language}
                        required
                        valueFormat={dateValueFormat}
                        minDate={dayjs().toDate()}
                        renderDay={DayRender}
                        {...form.getInputProps('student_task_date')}
                      />
                      <RichTextArea
                        name='student_task_description'
                        required
                        size='md'
                        label={t('new_project_page.activity_deliverable_instructions_label')}
                        withAsterisk
                        {...form.getInputProps('student_task_description')}
                        onChange={updateField('student_task_description')}
                      ></RichTextArea>
                      {temporaryTaskId.length > 0 && projectID !== null && (
                        <>
                          <WrappTaskFiles key={temporaryTaskId} taskId={temporaryTaskId} projectId={projectID} />
                        </>
                      )}
                    </Stack>
                  )}
                </Transition>
              </Stack>

              <Box className={classes.footer}>
                <Grid gutter={isMobile ? 'sm' : 'md'} justify='right' p={0} m={0}>
                  <Grid.Col span={isMobile ? 12 : 'content'}>
                    <Button fullWidth={isMobile} variant='default' size='lg' onClick={onClose}>
                      {t('general_buttons_cancel')}
                    </Button>
                  </Grid.Col>
                  <Grid.Col span={isMobile ? 12 : 'content'}>
                    <Button
                      type='button'
                      fullWidth={isMobile}
                      variant='filled'
                      size='lg'
                      onClick={() => handleForm(form.values)}
                    >
                      {t('general_buttons_save')}
                    </Button>
                  </Grid.Col>
                </Grid>
              </Box>
            </FocusTrap>
          </div>
        </>
      )}
    </Modal>
  )
}
